import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import * as api from '../../api';

import { Button } from '../../components/Button';
import { onKeyPress } from '../../utils/keypress';

import styles from './index.module.css';

interface Props {
  selectedLanguage: api.Language.Language;
  supportedLanguages: api.Language.Language[];
  onLanguageSelected: (language: api.Language.Language) => void;
  languageModalWrapper: React.RefObject<HTMLDivElement>;
  languageModalButtons: React.RefObject<HTMLButtonElement>[];
}

const LanguageSelection: React.FC<Props> = ({
  selectedLanguage,
  supportedLanguages,
  onLanguageSelected,
  languageModalWrapper,
  languageModalButtons,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { t } = useTranslation();
  if (!supportedLanguages.length) return null;

  return (
    <>
      {isModalOpen && (
        <div
          className={styles.ModalWrapper}
          onClick={() => setIsModalOpen(false)}
          role={'dialog'}
          ref={languageModalWrapper}
          tabIndex={1}
        >
          <div
            className={styles.ModalContent}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.Instructions}>{t('Select a language')}</div>
            <div>
              {[api.Language.Language.En, ...supportedLanguages]
                .sort((lang1, lang2) =>
                  api.Language.languageMap[lang1].localeCompare(
                    api.Language.languageMap[lang2],
                  ),
                )
                .map((lang, key) => (
                  <Button
                    key={lang}
                    children={t(api.Language.languageMap[lang])}
                    className={styles.PickLanguageButton}
                    onClick={() => {
                      onLanguageSelected(lang);
                      setIsModalOpen(false);
                    }}
                    tabIndex={1}
                    buttonRef={languageModalButtons[key]}
                  />
                ))}
              <Button
                children={t('Close')}
                className={styles.PickLanguageButton}
                onClick={() => setIsModalOpen(false)}
                tabIndex={1}
                buttonRef={languageModalButtons[supportedLanguages.length + 1]}
              />
            </div>
          </div>
        </div>
      )}
      <div className={styles.LanguageSelection}>
        <span
          role="button"
          tabIndex={isModalOpen ? -1 : 0}
          aria-label={t('Change Language')}
          className={styles.ChangeLanguage}
          onClick={() => setIsModalOpen(true)}
          onKeyDown={(e) =>
            onKeyPress({
              e,
              onConfirm: () => setIsModalOpen(true),
            })
          }
        >
          {t('Change Language')}: {api.Language.languageMap[selectedLanguage]}
        </span>
      </div>
    </>
  );
};

export default LanguageSelection;
